import { useTranslation } from '@core/i18n';
import { cn } from '@core/utils/cn';

export type TrackerStatus =
  'moving' | 'idle' | 'stale' | 'offline' | 'unreported' | 'alert' | 'lowBattery';

/**
 * Literal class strings, not `bg-status-${status}`.
 *
 * Tailwind scans source text statically and never evaluates a template literal, so a
 * runtime-built class emits no CSS and the dot renders invisible -- with no error.
 * `satisfies Record<TrackerStatus, ...>` then makes adding a status a compile error
 * rather than a blank pin. See DESIGN_LANGUAGE.md 13.1.
 */
const DOT = {
  moving: 'bg-status-moving',
  idle: 'bg-status-idle',
  // Shape carries the meaning too: a dashed ring for stale, a dotted outline for
  // offline. Colour is never the only channel.
  stale: 'bg-transparent ring-2 ring-status-stale',
  offline: 'bg-transparent border-2 border-dotted border-status-offline',
  unreported: 'bg-transparent border-2 border-dashed border-status-unreported',
  alert: 'bg-status-alert',
  lowBattery: 'bg-status-lowbattery',
} as const satisfies Record<TrackerStatus, string>;

/**
 * The labels live in the message catalogue, keyed by the same union.
 *
 * <p>They appear on five screens and inside the map pins' accessible names, which is
 * exactly why they are not per-feature strings: one mistranslation would otherwise have
 * to be fixed in six places, and the sixth is the one a screen reader reads.
 */
export function useStatusLabel(): (status: TrackerStatus) => string {
  const { t } = useTranslation();
  return (status) => t.status[status];
}

export interface StatusDotProps {
  status: TrackerStatus;
  className?: string;
  /** Renders the label alongside. Without it the dot carries an aria-label instead. */
  withLabel?: boolean;
}

export function StatusDot({ status, className, withLabel = false }: StatusDotProps) {
  const label = useStatusLabel()(status);

  const dot = (
    <span
      className={cn('inline-block size-2.5 shrink-0 rounded-full', DOT[status], className)}
      aria-hidden={withLabel}
      aria-label={withLabel ? undefined : label}
      role={withLabel ? undefined : 'img'}
    />
  );

  if (!withLabel) return dot;
  return (
    <span className="inline-flex items-center gap-2">
      {dot}
      <span className="text-[13px]">{label}</span>
    </span>
  );
}
